import React, { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../../store/authStore';
import api from '../../services/api';
import AIMetricAssistant from '../../components/data/AIMetricAssistant';

// Measure types
const MEASURE_TYPES = [
  { value: 'count', label: 'Count', description: 'Count all rows' },
  { value: 'countDistinct', label: 'Count Distinct', description: 'Count unique values' },
  { value: 'sum', label: 'Sum', description: 'Sum of values' },
  { value: 'avg', label: 'Average', description: 'Average of values' },
  { value: 'min', label: 'Minimum', description: 'Minimum value' },
  { value: 'max', label: 'Maximum', description: 'Maximum value' }
];

// Format types
const FORMAT_TYPES = [
  { value: 'number', label: 'Number' },
  { value: 'percent', label: 'Percentage' },
  { value: 'currency', label: 'Currency' }
];

// Categories
const CATEGORIES = [
  'general',
  'revenue',
  'customers',
  'operations',
  'performance',
  'growth'
];

const MetricBuilder = () => {
  const { getCurrentCompanyId, getCurrentCompany } = useAuthStore();
  const companyId = getCurrentCompanyId();
  const currentCompany = getCurrentCompany();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [metrics, setMetrics] = useState([]);
  const [dataSources, setDataSources] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingMetric, setEditingMetric] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const [aiGeneratedMetric, setAiGeneratedMetric] = useState(null);

  // Fetch metrics and data sources
  const fetchData = useCallback(async () => {
    if (!companyId) {
      setError('Please select a company first');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const [metricsRes, dsRes] = await Promise.all([
        api.get('/metrics', { params: { companyId } }).catch(() => ({ data: { metrics: [] } })),
        api.get('/data-sources', { params: { companyId } })
      ]);
      
      setMetrics(metricsRes.data.metrics || []);
      // Data sources API returns paginated response with 'data' property
      setDataSources(dsRes.data.data || dsRes.data.dataSources || []);
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Delete metric
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this metric?')) return;
    
    try {
      await api.delete(`/metrics/${id}`);
      setMetrics(prev => prev.filter(m => m._id !== id));
      setSuccess('Metric deleted');
    } catch (err) {
      setError('Failed to delete metric');
    }
  };

  // Toggle metric active status
  const handleToggleActive = async (id, isActive) => {
    try {
      await api.put(`/metrics/${id}`, { isActive: !isActive });
      setMetrics(prev => prev.map(m => 
        m._id === id ? { ...m, isActive: !isActive } : m
      ));
    } catch (err) {
      setError('Failed to update metric');
    }
  };

  // Open edit modal
  const handleEdit = (metric) => {
    setEditingMetric(metric);
    setShowModal(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setEditingMetric(null);
    setAiGeneratedMetric(null);
    setShowModal(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Show message if no company selected (for super admins)
  if (!companyId) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-6 text-center">
          <svg className="mx-auto h-12 w-12 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="mt-4 text-lg font-semibold text-amber-800 dark:text-amber-300">No Company Selected</h3>
          <p className="mt-2 text-amber-700 dark:text-amber-400">
            Please select a company from the company selector to view and manage metrics.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <svg className="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          Business Metrics
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400 max-w-3xl">
          Define the key numbers you want to track, like "Total Revenue" or "Customer Count". 
          These metrics will be available when creating charts and dashboards.
          {currentCompany && (
            <span className="block mt-1 text-sm text-blue-600 dark:text-blue-400">
              Company: {currentCompany.name || currentCompany}
            </span>
          )}
        </p>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
          {error}
          <button onClick={() => setError(null)} className="float-right">&times;</button>
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-400">
          {success}
          <button onClick={() => setSuccess(null)} className="float-right">&times;</button>
        </div>
      )}

      {/* Actions */}
      <div className="mb-6 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {metrics.length} metrics defined
          </span>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowAIAssistant(true)}
            className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white rounded-lg flex items-center gap-2 transition-all shadow-lg hover:shadow-blue-500/25"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            AI Assistant
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Metric
          </button>
        </div>
      </div>

      {/* Metrics Grid */}
      {metrics.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
          <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">No metrics defined</h3>
          <p className="mt-2 text-gray-500 dark:text-gray-400">
            Create your first metric to enable AI-powered business analysis.
          </p>
          <button
            onClick={() => setShowModal(true)}
            className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
          >
            Create Metric
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {metrics.map((metric) => (
            <MetricCard
              key={metric._id}
              metric={metric}
              onEdit={() => handleEdit(metric)}
              onDelete={() => handleDelete(metric._id)}
              onToggleActive={() => handleToggleActive(metric._id, metric.isActive)}
            />
          ))}
        </div>
      )}

      {/* Metric Modal */}
      {showModal && (
        <MetricModal
          metric={editingMetric || aiGeneratedMetric}
          dataSources={dataSources}
          onClose={handleCloseModal}
          onSave={async (data) => {
            try {
              setSaving(true);
              if (editingMetric) {
                await api.put(`/metrics/${editingMetric._id}`, { ...data, companyId });
                setSuccess('Metric updated');
              } else {
                await api.post('/metrics', { ...data, companyId });
                setSuccess('Metric created');
              }
              handleCloseModal();
              await fetchData();
            } catch (err) {
              setError(err.response?.data?.error || 'Failed to save metric');
            } finally {
              setSaving(false);
            }
          }}
          saving={saving}
        />
      )}

      {/* AI Metric Assistant */}
      {showAIAssistant && (
        <AIMetricAssistant
          companyId={companyId}
          onClose={() => setShowAIAssistant(false)}
          onMetricGenerated={(metricConfig) => {
            setShowAIAssistant(false);
            setAiGeneratedMetric(metricConfig);
            setShowModal(true);
          }}
        />
      )}
    </div>
  );
};

// Metric Card Component
const MetricCard = ({ metric, onEdit, onDelete, onToggleActive }) => {
  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow border ${
      metric.isActive 
        ? 'border-transparent' 
        : 'border-gray-300 dark:border-gray-600 opacity-60'
    }`}>
      <div className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${
              getCategoryColor(metric.category)
            } mb-2`}>
              {metric.category}
            </span>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {metric.displayName}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 font-mono">
              {metric.name}
            </p>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={onToggleActive}
              className={`p-1 rounded ${
                metric.isActive 
                  ? 'text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20' 
                  : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              title={metric.isActive ? 'Deactivate' : 'Activate'}
            >
              <svg className="w-5 h-5" fill={metric.isActive ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
          </div>
        </div>

        <p className="mt-3 text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
          {metric.description}
        </p>

        <div className="mt-4 flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
          <span className="flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
            </svg>
            {metric.sourceModel}
          </span>
          <span className="flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
            </svg>
            {metric.measure?.type}
          </span>
        </div>

        {metric.aiHints?.synonyms?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {metric.aiHints.synonyms.slice(0, 3).map((syn, i) => (
              <span key={i} className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 rounded">
                {syn}
              </span>
            ))}
            {metric.aiHints.synonyms.length > 3 && (
              <span className="px-2 py-0.5 text-xs text-gray-500">
                +{metric.aiHints.synonyms.length - 3} more
              </span>
            )}
          </div>
        )}
      </div>

      <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-700 flex justify-end gap-2">
        <button
          onClick={onEdit}
          className="px-3 py-1.5 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
        >
          Edit
        </button>
        <button
          onClick={onDelete}
          className="px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
        >
          Delete
        </button>
      </div>
    </div>
  );
};

// Get category color
const getCategoryColor = (category) => {
  const colors = {
    general: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
    revenue: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    customers: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    operations: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    performance: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    growth: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400'
  };
  return colors[category] || colors.general;
};

// Metric Modal Component
const MetricModal = ({ metric, dataSources, onClose, onSave, saving }) => {
  const [formData, setFormData] = useState({
    name: metric?.name || '',
    displayName: metric?.displayName || '',
    description: metric?.description || '',
    category: metric?.category || 'general',
    sourceModel: metric?.sourceModel || '',
    measure: {
      type: metric?.measure?.type || 'count',
      sql: metric?.measure?.sql || '',
      format: metric?.measure?.format || 'number'
    },
    aiHints: {
      synonyms: metric?.aiHints?.synonyms?.join(', ') || '',
      whenToUse: metric?.aiHints?.whenToUse || '',
      unit: metric?.aiHints?.unit || 'count',
      unitLabel: metric?.aiHints?.unitLabel || ''
    }
  });

  // Auto-generate name from display name
  const handleDisplayNameChange = (value) => {
    setFormData(prev => ({
      ...prev,
      displayName: value,
      name: prev.name || value.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')
    }));
  };

  // Get source models from data sources
  const sourceModels = dataSources.map(ds => ({
    name: ds.bigQueryTable,
    label: ds.name
  }));

  const handleSubmit = () => {
    const submitData = {
      ...formData,
      aiHints: {
        ...formData.aiHints,
        synonyms: formData.aiHints.synonyms.split(',').map(s => s.trim()).filter(Boolean)
      }
    };
    onSave(submitData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 my-8">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {metric ? 'Edit Metric' : 'Create Metric'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Basic Info */}
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900 dark:text-white">Basic Information</h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Display Name *
                </label>
                <input
                  type="text"
                  value={formData.displayName}
                  onChange={(e) => handleDisplayNameChange(e.target.value)}
                  placeholder="e.g., Customer Churn Rate"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-700"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Internal Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., churn_rate"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-700 font-mono text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe what this metric measures and how it's calculated..."
                rows={2}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-700"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Category
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-700"
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Source Model *
                </label>
                <select
                  value={formData.sourceModel}
                  onChange={(e) => setFormData(prev => ({ ...prev, sourceModel: e.target.value }))}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-700"
                >
                  <option value="">Select a data source</option>
                  {sourceModels.map(m => (
                    <option key={m.name} value={m.name}>{m.label} ({m.name})</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Measure Definition */}
          <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <h4 className="font-medium text-gray-900 dark:text-white">Measure Definition</h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Aggregation Type *
                </label>
                <select
                  value={formData.measure.type}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    measure: { ...prev.measure, type: e.target.value } 
                  }))}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-700"
                >
                  {MEASURE_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Format
                </label>
                <select
                  value={formData.measure.format}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    measure: { ...prev.measure, format: e.target.value } 
                  }))}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-700"
                >
                  {FORMAT_TYPES.map(f => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                SQL Expression *
              </label>
              <input
                type="text"
                value={formData.measure.sql}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  measure: { ...prev.measure, sql: e.target.value } 
                }))}
                placeholder="e.g., {CUBE}.column_name or CASE WHEN ... END"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-700 font-mono text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                Use &#123;CUBE&#125; to reference the current cube context
              </p>
            </div>
          </div>

          {/* AI Hints */}
          <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <h4 className="font-medium text-gray-900 dark:text-white">AI Hints</h4>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Help the AI understand when and how to use this metric.
            </p>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Synonyms
              </label>
              <input
                type="text"
                value={formData.aiHints.synonyms}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  aiHints: { ...prev.aiHints, synonyms: e.target.value } 
                }))}
                placeholder="e.g., attrition rate, customer loss, churn (comma-separated)"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-700"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                When to Use
              </label>
              <input
                type="text"
                value={formData.aiHints.whenToUse}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  aiHints: { ...prev.aiHints, whenToUse: e.target.value } 
                }))}
                placeholder="e.g., When analyzing customer retention or subscription health"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-700"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Unit Type
                </label>
                <select
                  value={formData.aiHints.unit}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    aiHints: { ...prev.aiHints, unit: e.target.value } 
                  }))}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-700"
                >
                  <option value="count">Count</option>
                  <option value="percentage">Percentage</option>
                  <option value="currency">Currency</option>
                  <option value="duration">Duration</option>
                  <option value="rate">Rate</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Unit Label
                </label>
                <input
                  type="text"
                  value={formData.aiHints.unitLabel}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    aiHints: { ...prev.aiHints, unitLabel: e.target.value } 
                  }))}
                  placeholder="e.g., customers, orders, dollars"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-700"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || !formData.name || !formData.displayName || !formData.description || !formData.sourceModel || !formData.measure.sql}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg flex items-center gap-2"
          >
            {saving && <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>}
            {metric ? 'Save Changes' : 'Create Metric'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MetricBuilder;

